import { forwardRef, type ButtonHTMLAttributes } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidth?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, variant = 'primary', size = 'md', isLoading = false, fullWidth = false, className = '', disabled, ...props }, ref) => {
    const baseClasses = 'inline-flex items-center justify-center font-medium font-display rounded-full transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed';

    const variantClasses = {
      primary: 'bg-accent text-slate-950 hover:bg-accent-hover shadow-glow hover:shadow-glow-lg',
      secondary: 'bg-white/5 text-white border border-white/10 hover:bg-white/10 hover:border-white/20',
      danger: 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30',
    };

    const sizeClasses = { sm: 'px-4 py-1.5 text-sm', md: 'px-6 py-2.5 text-sm', lg: 'px-8 py-3 text-base' };
    const widthClasses = fullWidth ? 'w-full' : '';

    return (
      <button ref={ref} className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClasses} ${className}`} disabled={disabled || isLoading} {...props}>
        {isLoading ? (
          <>
            <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin mr-2" />
            <span>Loading...</span>
          </>
        ) : children}
      </button>
    );
  }
);

Button.displayName = 'Button';
export default Button;
